// AppointmentModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch, useSelector } from 'react-redux';
import { createUserAppointment } from 'apiServices/reducer/dialogApiService';
import { getLocations, addLocation, removeLocation } from 'apiServices/reducer/apiService';
import '../../../styles/user.css';  
import CustomSuccessModal from '../../../components/CustomSuccessModal'; 

import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";

// Helper function to add minutes to a Date object
const addMinutes = (date, minutes) => {
    if (!date) return null;
    const newDate = new Date(date);
    newDate.setMinutes(newDate.getMinutes() + minutes);
    return newDate;
};

// Helper function to get minimum time for today's appointments
const getMinTimeForToday = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // Round up to the next 15-minute interval
    const roundedMinute = Math.ceil(currentMinute / 15) * 15;
    let adjustedHour = currentHour;
    
    if (roundedMinute === 60) {
        adjustedHour += 1;
        return `${String(adjustedHour).padStart(2, '0')}:00`;
    }
    
    return `${String(adjustedHour).padStart(2, '0')}:${String(roundedMinute).padStart(2, '0')}`;
};

const AppointmentModal = ({ show, onHide, rowData = {}, lstLocations = [], onSave, centered, userId }) => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.apiService?.token);
    const [fieldErrors, setFieldErrors] = useState({ location_name: false, appt_date: false, appt_start: false, appt_end: false });
    const inputErrorStyle = { border: '1px solid #dc3545', boxShadow: 'none' };
    const [formData, setFormData] = useState({
        location_name: '',
        appt_date: null,
        appt_start: null,
        appt_end: null,
        ApptLocID: '',
    });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isStartTimeDropdownOpen, setIsStartTimeDropdownOpen] = useState(false);
    const [isEndTimeDropdownOpen, setIsEndTimeDropdownOpen] = useState(false);
    const [startTimeSearch, setStartTimeSearch] = useState('');
    const [endTimeSearch, setEndTimeSearch] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    
    // Separate state variables for different success modals (like userLandingPage.jsx)
    const [showLocationSuccessModal, setShowLocationSuccessModal] = useState(false);
    const [locationSuccessMessage, setLocationSuccessMessage] = useState('');
    const [showAppointmentSuccessModal, setShowAppointmentSuccessModal] = useState(false);
    const [appointmentSuccessMessage, setAppointmentSuccessMessage] = useState('');
    
    // Flags to prevent double execution (like userLandingPage.jsx)
    const [isLocationProcessing, setIsLocationProcessing] = useState(false);
    const [isAppointmentProcessing, setIsAppointmentProcessing] = useState(false);
    
    // Refs to track modal state (like userLandingPage.jsx)
    const locationModalShownRef = useRef(false);
    const appointmentModalShownRef = useRef(false);
    
    // State for location error handling
    const [locationError, setLocationError] = useState('');
    
    useEffect(() => {
        if (show) {
            setFieldErrors({ location_name: false, appt_date: false, appt_start: false, appt_end: false });
            // Reset all modal states when modal opens
            setShowLocationSuccessModal(false);
            setShowAppointmentSuccessModal(false);
            setShowSuccessModal(false);
            setIsLocationProcessing(false);
            setIsAppointmentProcessing(false);
            locationModalShownRef.current = false;
            appointmentModalShownRef.current = false;
            setLocationError('');
        }
    }, [show]);

    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newLocationName, setNewLocationName] = useState('');

    useEffect(() => {
        if (userId && token) {
            dispatch(getLocations({ user_id: userId, token }))
                .unwrap()
                .then((data) => { 
                    setLocations(data);
                })
                .catch((error) => {
                    console.error('Error fetching locations:', error); 
                });
        }
    }, [userId, token, dispatch]);

    useEffect(() => {
        if (rowData) {
            setFormData({
                location_name: rowData?.location_name || '',
                appt_date: rowData?.appt_date ? new Date(rowData.appt_date) : null,
                appt_start: rowData?.appt_start ? new Date(`1970-01-01T${rowData.appt_start}`) : null,
                appt_end: rowData?.appt_end ? new Date(`1970-01-01T${rowData.appt_end}`) : null,
                ApptLocID: rowData?.ApptLocID || '',
            });
            if (rowData?.location_name) {
                setSelectedLocation({
                    location_id: rowData.ApptLocID,
                    location_name: rowData.location_name
                });
            }
        }
    }, [rowData]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => {
            // If start time changes, reset end time if it is before or equal to new start time
            if (field === 'appt_start') {
                if (prev.appt_end && value && value >= prev.appt_end) {
                    return {
                        ...prev,
                        [field]: value,
                        appt_end: null,
                    };
                }
            }
            
            // If end time changes, ensure it's not the same as start time
            if (field === 'appt_end') {
                if (prev.appt_start && value && value <= prev.appt_start) {
                    // Don't allow end time to be same as or before start time
                    return prev;
                }
            }
            
            return {
                ...prev,
                [field]: value,
            };
        });
    };

    const formatTime = (date) => {
        if (!date) return '';
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    const formatDate = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Generate time options for dropdown (15-minute intervals)
    const generateTimeOptions = (type) => {
        const options = [];
        const isToday = formData.appt_date && new Date(formData.appt_date).toDateString() === new Date().toDateString();
        const now = new Date();
        
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const time = new Date();
                time.setHours(hour, minute, 0, 0);
                
                // For start time, filter out past times if it's today
                if (type === 'start' && isToday) {
                    const currentTime = now.getHours() * 60 + now.getMinutes();
                    const optionTime = hour * 60 + minute;
                    if (optionTime <= currentTime) continue;
                }
                
                // For end time, filter out times before or equal to start time
                if (type === 'end' && formData.appt_start) {
                    const startTime = formData.appt_start.getHours() * 60 + formData.appt_start.getMinutes();
                    const optionTime = hour * 60 + minute;
                    if (optionTime <= startTime) continue;
                }
                
                const timeLabel = formatTime(time);
                
                // Apply search filter
                const searchTerm = type === 'start' ? startTimeSearch : endTimeSearch;
                if (searchTerm && !timeLabel.toLowerCase().includes(searchTerm.toLowerCase())) {
                    continue;
                }
                
                options.push({
                    value: time,
                    label: timeLabel
                });
            }
        }
        
        return options;
    };

    // Handle time selection from dropdown
    const handleTimeSelect = (field, timeValue) => {
        handleInputChange(field, timeValue);
        setFieldErrors(prev => ({ ...prev, [field]: false }));
        
        // Close the appropriate dropdown
        if (field === 'appt_start') {
            setIsStartTimeDropdownOpen(false);
        } else if (field === 'appt_end') {
            setIsEndTimeDropdownOpen(false);
        }
    };

    const handleSelectLocation = (location) => {
        setSelectedLocation(location);
        setFormData((prev) => ({
            ...prev,
            location_name: location.location_name,
            ApptLocID: location.location_id,
        }));
        if (location.location_name) {
            setFieldErrors(prev => ({ ...prev, location_name: false }));
        }
        setIsDropdownOpen(false);
    };

    const handleCancelNewLocation = () => {
        setIsAddingNew(false);
        setNewLocationName('');
        setLocationError('');
    };

    const handleAddNewLocation = () => {
        if (!newLocationName.trim()) { 
            return;
        }
        
        // Clear any previous error
        setLocationError('');
        
        // Check if location already exists in the current list
        const locationExists = locations.some(location => 
            location.location_name.toLowerCase().trim() === newLocationName.toLowerCase().trim()
        );
        
        if (locationExists) {
                setLocationError('Location already exists.');
            return;
        }
        
        // Prevent double execution (like userLandingPage.jsx)
        if (isLocationProcessing || locationModalShownRef.current) {
            console.log("Location already in progress or modal already shown, skipping...");
            return;
        }
        
        setIsLocationProcessing(true);
        locationModalShownRef.current = true;
        
        // Reset modal state first to prevent double showing (like userLandingPage.jsx)
        setShowLocationSuccessModal(false);
        setShowAppointmentSuccessModal(false);
        setShowSuccessModal(false);
        
        dispatch(addLocation({ 
            user_id: userId, 
            location_name: newLocationName.trim(), 
            token 
        }))
            .unwrap()
            .then((response) => { 
                if (response) {
                    // Reset form and refresh locations like Angular code
                    setIsAddingNew(false);
                    setNewLocationName('');
                    
                    // Use setTimeout to ensure state is properly reset before showing modal (like userLandingPage.jsx)
                    setTimeout(() => {
                        setLocationSuccessMessage('New location added successfully');
                        setShowLocationSuccessModal(true);
                        setIsLocationProcessing(false);
                    }, 100);
                    
                    // Refresh locations list like Angular code does
                    dispatch(getLocations({ user_id: userId, token }))
                        .unwrap()
                        .then((data) => {
                            console.log('Refreshed locations:', data);
                            setLocations(data);
                        })
                        .catch((error) => {
                            console.error('Error refreshing locations:', error);
                        });
                } else {
                    // Handle case where backend returns false (location already exists)
                    setLocationError('Location already exists.');
                    setIsLocationProcessing(false);
                    locationModalShownRef.current = false;
                }
            })
            .catch((error) => {
                console.error('Error adding location:', error);
                setLocationError('Error adding location');
                setIsLocationProcessing(false);
                locationModalShownRef.current = false;
            });
    };

    const handleRemoveLocation = (location) => {
        if (!location.location_id) return;
        
        // Prevent double execution (like userLandingPage.jsx)
        if (isLocationProcessing || locationModalShownRef.current) {
            console.log("Location removal already in progress or modal already shown, skipping...");
            return;
        }
        
        setIsLocationProcessing(true);
        locationModalShownRef.current = true;
        
        // Reset modal state first to prevent double showing (like userLandingPage.jsx)
        setShowLocationSuccessModal(false);
        setShowAppointmentSuccessModal(false);
        setShowSuccessModal(false);
        
        dispatch(removeLocation({ 
            user_id: userId, 
            location_name: location.location_name,
            token 
        }))
            .unwrap()
            .then((response) => { 
                if (response) {
                    // Remove from local state like Angular code
                    setLocations(prev => prev.filter(loc => loc.location_id !== location.location_id));
                    
                    // Clear selection if removed location was selected
                    if (selectedLocation?.location_id === location.location_id) {
                        setSelectedLocation(null);
                        setFormData(prev => ({
                            ...prev,
                            location_name: '',
                            ApptLocID: ''
                        }));
                    }
                    
                    // Use setTimeout to ensure state is properly reset before showing modal (like userLandingPage.jsx)
                    setTimeout(() => {
                        setLocationSuccessMessage('Location removed successfully');
                        setShowLocationSuccessModal(true);
                        setIsLocationProcessing(false);
                    }, 100);
                    
                    // Close dropdown like Angular code does
                    setIsDropdownOpen(false);
                } else {
                    setLocationSuccessMessage('Error removing location');
                    setShowLocationSuccessModal(true);
                    setIsLocationProcessing(false);
                }
            })
            .catch((error) => {
                console.error('Error removing location:', error);
                setLocationSuccessMessage('Error removing location');
                setShowLocationSuccessModal(true);
                setIsLocationProcessing(false);
            });
    }; 

    const onSaveAppointment = async (e) => {
        e.preventDefault();
        
        // Prevent double execution (like userLandingPage.jsx)
        if (isAppointmentProcessing || appointmentModalShownRef.current) {
            console.log("Appointment already in progress or modal already shown, skipping...");
            return;
        }
        
        // Check if start time and end time are the same
        if (formData.appt_start && formData.appt_end && formData.appt_start.getTime() === formData.appt_end.getTime()) {
            setAppointmentSuccessMessage('Start time and end time cannot be the same. Please select different times.');
            setShowAppointmentSuccessModal(true);
            return;
        }
        
        // Check if appointment is scheduled for past time on current date
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const isToday = formData.appt_date && formData.appt_date.getTime() === today.getTime();
        
        if (isToday && formData.appt_start) {
            const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes
            const appointmentTime = formData.appt_start.getHours() * 60 + formData.appt_start.getMinutes(); // Appointment time in minutes
            
            if (appointmentTime <= currentTime) {
                setAppointmentSuccessMessage('Cannot schedule appointment for past time slots on the current date. Please select a future time.');
                setShowAppointmentSuccessModal(true);
                return;
            }
        }
        
        const errors = {
            location_name: !formData.location_name?.trim(),
            appt_date: !formData.appt_date,
            appt_start: !formData.appt_start,
            appt_end: !formData.appt_end
        };
        setFieldErrors(errors);
        if (Object.values(errors).some(Boolean)) return;
        // if (!validateForm()) return;

        const formBody = {
            ApptID: rowData?.ApptID || "0",
            ApptLocID: formData.ApptLocID,
            ApptDate: formatDate(formData.appt_date),
            ApptStart: formatTime(formData.appt_start),
            ApptEnd: formatTime(formData.appt_end),
            UserID: userId,
            token,
        };
        
        // Only include newLocationName if we're actually adding a new location (no ApptLocID)
        if (!formData.ApptLocID && formData.location_name) {
            formBody.newLocationName = formData.location_name;
        }

        setIsAppointmentProcessing(true);
        appointmentModalShownRef.current = true;
        
        // Reset modal state first to prevent double showing (like userLandingPage.jsx)
        setShowLocationSuccessModal(false);
        setShowAppointmentSuccessModal(false);
        setShowSuccessModal(false);

        try {
            const response = await dispatch(createUserAppointment(formBody)).unwrap();
            
            // Use setTimeout to ensure state is properly reset before showing modal (like userLandingPage.jsx)
            setTimeout(() => {
                setAppointmentSuccessMessage(response?.OutputMessage || 'Appointment saved successfully!');
                setShowAppointmentSuccessModal(true);
                setIsAppointmentProcessing(false);
            }, 100);
            if (onSave) {
                onSave({
                    ...formData,
                    appt_date: formBody.ApptDate,
                    appt_start: formBody.ApptStart,
                    appt_end: formBody.ApptEnd,
                });
            }
            // Notify calendar / other listeners to refresh without a full page reload
            try {
                window.dispatchEvent(new CustomEvent('pmr:appointments-changed', {
                    detail: {
                        action: 'save',
                        appointment: {
                            appt_date: formBody.ApptDate,
                            appt_start: formBody.ApptStart,
                            appt_end: formBody.ApptEnd,
                            location_name: formData.location_name,
                            ApptLocID: formBody.ApptLocID,
                        },
                    },
                }));
            } catch (e) { /* ignore */ }
            setFormData({
                location_name: '',
                appt_date: null,
                appt_start: null,
                appt_end: null,
                ApptLocID: '',
            });
            setSelectedLocation(null);
            onHide();
            setIsDropdownOpen(false);
        } catch (error) {
            console.error('API Error:', error);
            setIsAppointmentProcessing(false);
            appointmentModalShownRef.current = false;
        }
    };

    const resetAppointmentModalState = () => {
        setFormData({
            location_name: '',
            appt_date: null,
            appt_start: null,
            appt_end: null,
            ApptLocID: '',
        });
        setSelectedLocation(null);
        setIsAddingNew(false);
        setNewLocationName('');
        setIsStartTimeDropdownOpen(false);
        setIsEndTimeDropdownOpen(false);
        setStartTimeSearch('');
        setEndTimeSearch('');
        setFieldErrors({ location_name: false, appt_date: false, appt_start: false, appt_end: false });
        
        // Reset all modal states (like userLandingPage.jsx)
        setShowLocationSuccessModal(false);
        setShowAppointmentSuccessModal(false);
        setShowSuccessModal(false);
        setIsLocationProcessing(false);
        setIsAppointmentProcessing(false);
        locationModalShownRef.current = false;
        appointmentModalShownRef.current = false;
        setLocationError('');
    };

    return (
        <>
            <Modal show={show} onHide={() => { resetAppointmentModalState(); onHide(); }} centered={centered} dialogClassName="upcoming-appointments-modal" backdrop="static" keyboard={false} enforceFocus={false}>
                <Modal.Header closeButton className="modal-header-bg">
                    <Modal.Title className='text-capitalize'>{rowData?.ApptID ? 'Edit Appointment' : 'Add Upcoming Appointment'}</Modal.Title>
                </Modal.Header>

                <Form onSubmit={onSaveAppointment} autoComplete="off">
                    <Modal.Body>
                        <Form.Group className="mb-3" controlId="locationName">
                            <Form.Label>Location:</Form.Label>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                                {!isAddingNew ? (
                                    <>
                                        <Dropdown
                                            className="flex-grow-1"
                                            show={isDropdownOpen}
                                            onToggle={(isOpen) => setIsDropdownOpen(isOpen)}
                                        >
                                            <Dropdown.Toggle
                                                variant="outline-primary"
                                                id="dropdown-location"
                                                className={`w-100 text-center text-capitalize ${fieldErrors.location_name ? 'error-border' : ''}`}
                                                style={{
                                                    background: '#fff',
                                                    border: '1.5px solid var(--pmr-primary)',
                                                    color: selectedLocation?.location_name ? 'var(--pmr-text-heading)' : 'var(--pmr-text-muted)',
                                                    fontWeight: 500,
                                                    minHeight: '32px',
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                                onClick={() => {
                                                    setIsDropdownOpen(!isDropdownOpen);
                                                    if (selectedLocation?.location_name) {
                                                        setFieldErrors(prev => ({ ...prev, location_name: false }));
                                                    }
                                                }}
                                            >
                                                {selectedLocation?.location_name || 'Select Location'}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu
                                                className="w-100"
                                                style={{
                                                    background: '#fff',
                                                    border: '1px solid #bfc9d9',
                                                    borderRadius: '12px',
                                                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                                    marginTop: '4px',
                                                    padding: 0,
                                                }}
                                            >
                                                {locations.length === 0 ? (
                                                    <div
                                                        style={{
                                                            padding: '15px',
                                                            textAlign: 'center',
                                                            color: 'var(--pmr-primary)',
                                                            fontSize: '14px',
                                                            borderBottom: '1px solid #f1f1f1'
                                                        }}
                                                    >
                                                        <div style={{ marginBottom: '8px', fontWeight: 600 }}>
                                                            Locations are yet to be added
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#d32f2f', fontWeight: 700, fontStyle: 'italic' }}>
                                                            (Click on + icon to add your new location)
                                                        </div>
                                                    </div>
                                                ) : (
                                                    locations.map((location) => (
                                                        <div
                                                            key={location.location_id}
                                                            className="d-flex justify-content-between align-items-center px-3"
                                                            style={{
                                                                padding: '10px 0',
                                                                borderBottom: '1px solid #f1f1f1',
                                                                cursor: 'pointer',
                                                                fontWeight: 500,
                                                                color: 'var(--pmr-text-heading)',
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.background = '#f6faff'}
                                                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                                        >
                                                            <span
                                                                onClick={() => handleSelectLocation(location)}
                                                                style={{
                                                                    flex: 1,
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                {location.location_name || 'Unnamed'}
                                                            </span>
                                                            <span
                                                                onClick={e => { e.stopPropagation(); handleRemoveLocation(location); }}
                                                                style={{
                                                                    color: 'var(--pmr-primary)',
                                                                    fontSize: '20px',
                                                                    marginLeft: '12px',
                                                                    cursor: 'pointer',
                                                                    fontWeight: 700,
                                                                    lineHeight: 1,
                                                                }}
                                                            >
                                                                ×
                                                            </span>
                                                        </div>
                                                    ))
                                                )}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                        <Button
                                            variant="primary"
                                            style={{
                                                borderRadius: '8px',
                                                minWidth: '38px',
                                                minHeight: '38px',
                                                fontWeight: 700,
                                                fontSize: '20px',
                                                padding: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: 'var(--pmr-primary)',
                                                borderColor: 'var(--pmr-primary)',
                                                color: 'var(--pmr-primary)',
                                            }}
                                            onClick={() => setIsAddingNew(true)}
                                        >
                                            +
                                        </Button>
                                    </>
                                ) : (
                                    <div className="input-group flex-grow-1" style={{ maxWidth: '100%' }}>
                                        <Form.Control
                                            type="text"
                                            value={newLocationName}
                                            onChange={(e) => setNewLocationName(e.target.value)}
                                            placeholder="Enter new location"
                                            className="flex-grow-1"
                                            style={{ height: '32px', borderRight: 'none', boxShadow: 'none' }}
                                        />
                                        <Button
                                            variant="outline-success"
                                            onClick={handleAddNewLocation}
                                            title="Save"
                                            style={{
                                                height: '32px',
                                                borderTopLeftRadius: 0,
                                                borderBottomLeftRadius: 0,
                                                borderRight: 'none',
                                                boxShadow: 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0 12px'
                                            }}
                                        >
                                            <i className="fa fa-check"></i>
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            onClick={handleCancelNewLocation}
                                            title="Cancel"
                                            style={{
                                                height: '32px',
                                                borderTopLeftRadius: 0,
                                                borderBottomLeftRadius: 0,
                                                borderTopRightRadius: '8px',
                                                borderBottomRightRadius: '8px',
                                                boxShadow: 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0 12px'
                                            }}
                                        >
                                            <i className="fa fa-times"></i>
                                        </Button>
                                    </div>
                                )}
                            </div>
                            {isAddingNew && (
                                <div style={{ 
                                    fontSize: '12px', 
                                    color: '#d32f2f', 
                                    marginTop: '5px',
                                    fontStyle: 'italic',
                                    fontWeight: 600,
                                }}>
                                    Please click ✓ symbol to add new location.
                                </div>
                            )}
                            {locationError && (
                                <div style={{ 
                                    fontSize: '12px', 
                                    color: '#d32f2f', 
                                    marginTop: '5px',
                                    fontWeight: 600,
                                }}>
                                    {locationError}
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="apptDate">
                            <Form.Label className="d-block mb-1">Appointment Date:</Form.Label>
                            <Flatpickr
                                value={formData.appt_date}
                                onClose={(selectedDates) => {
                                    if (selectedDates && selectedDates[0]) {
                                        handleInputChange('appt_date', selectedDates[0]);
                                        setFieldErrors(prev => ({ ...prev, appt_date: false }));
                                    }
                                }}
                                options={{
                                    minDate: "today",
                                    dateFormat: "m/d/Y", 
                                    theme: "material_green"
                                }}
                                className={`form-control custom-textarea flat-datepicker ${fieldErrors.appt_date ? 'error-border' : ''}`}
                                style={fieldErrors.appt_date ? inputErrorStyle : {}}
                                placeholder="Select date"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="apptStart">
                            <Form.Label className="d-block mb-1">Start Time:</Form.Label>
                            <Dropdown
                                show={isStartTimeDropdownOpen}
                                onToggle={(isOpen) => setIsStartTimeDropdownOpen(isOpen)}
                                drop="up"
                            >
                                <Dropdown.Toggle
                                    variant="outline-primary"
                                    className={`w-100 text-center appt-time-toggle ${fieldErrors.appt_start ? 'error-border' : ''}`}
                                    style={{
                                        background: '#fff',
                                        border: '1.5px solid var(--pmr-primary)',
                                        color: 'var(--pmr-text-heading)',
                                        fontWeight: 500,
                                        minHeight: '32px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {formData.appt_start ? formatTime(formData.appt_start) : 'Select Start Time'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu
                                    className="w-100 appt-time-menu"
                                    style={{
                                        maxHeight: '200px',  
                                    }}
                                >
                                    {/* Search input for start time - Fixed at top */}
                                    <div style={{ 
                                        padding: '10px 15px', 
                                        borderBottom: '1px solid #f1f1f1',
                                        backgroundColor: '#fff',
                                        zIndex: 1
                                    }}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search time (e.g., 2:30, PM, AM)"
                                            value={startTimeSearch}
                                            onChange={(e) => setStartTimeSearch(e.target.value)}
                                            style={{
                                                border: '1px solid #bfc9d9',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                padding: '6px 10px'
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                    {/* Scrollable time options container */}
                                    <div style={{
                                        maxHeight: '150px',
                                        overflowY: 'auto'
                                    }}>
                                    {generateTimeOptions('start').map((timeOption) => (
                                        <Dropdown.Item
                                            key={timeOption.value}
                                            onClick={() => handleTimeSelect('appt_start', timeOption.value)}
                                            style={{
                                                padding: '10px 15px',
                                                borderBottom: '1px solid #f1f1f1',
                                                cursor: 'pointer',
                                                fontWeight: 500,
                                                color: 'var(--pmr-text-heading)',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f6faff'}
                                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                        >
                                            {timeOption.label}
                                        </Dropdown.Item>
                                    ))}
                                    {generateTimeOptions('start').length === 0 && startTimeSearch && (
                                        <div style={{ padding: '10px 15px', color: '#666', fontStyle: 'italic' }}>
                                            No times found matching "{startTimeSearch}"
                                        </div>
                                    )}
                                    </div>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="apptEnd">
                            <Form.Label className="d-block mb-1">End Time:</Form.Label>
                            <Dropdown
                                show={isEndTimeDropdownOpen}
                                onToggle={(isOpen) => setIsEndTimeDropdownOpen(isOpen)}
                                drop="up"
                            >
                                <Dropdown.Toggle
                                    variant="outline-primary"
                                    className={`w-100 text-center appt-time-toggle ${fieldErrors.appt_end ? 'error-border' : ''}`}
                                    style={{
                                        background: '#fff',
                                        border: '1.5px solid var(--pmr-primary)',
                                        color: 'var(--pmr-text-heading)',
                                        fontWeight: 500,
                                        minHeight: '32px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {formData.appt_end ? formatTime(formData.appt_end) : 'Select End Time'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu
                                    className="w-100 appt-time-menu"
                                    // style={{
                                    //     background: '#fff',
                                    //     border: '1px solid #bfc9d9',
                                    //     borderRadius: '12px',
                                    //     boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                    //     marginTop: '4px',
                                    //     padding: 0,
                                    //     maxHeight: '200px',
                                    //     overflowY: 'hidden',
                                    //     position: 'relative'
                                    // }}
                                    style={{
                                        maxHeight: '221px', 
                                    }}
                                >
                                    {/* Search input for end time - Fixed at top */}
                                    <div style={{ 
                                        padding: '10px 15px', 
                                        borderBottom: '1px solid #f1f1f1',
                                        backgroundColor: '#fff',
                                        zIndex: 1
                                    }}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search time (e.g., 2:30, PM, AM)"
                                            value={endTimeSearch}
                                            onChange={(e) => setEndTimeSearch(e.target.value)}
                                            style={{
                                                border: '1px solid #bfc9d9',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                padding: '6px 10px'
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                    {/* Scrollable time options container */}
                                    <div style={{
                                        maxHeight: '150px',
                                        overflowY: 'auto'
                                    }}>
                                    {generateTimeOptions('end').map((timeOption) => (
                                        <Dropdown.Item
                                            key={timeOption.value}
                                            onClick={() => handleTimeSelect('appt_end', timeOption.value)}
                                            style={{
                                                padding: '10px 15px',
                                                borderBottom: '1px solid #f1f1f1',
                                                cursor: 'pointer',
                                                fontWeight: 500,
                                                color: 'var(--pmr-text-heading)',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f6faff'}
                                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                        >
                                            {timeOption.label}
                                        </Dropdown.Item>
                                    ))}
                                    {generateTimeOptions('end').length === 0 && endTimeSearch && (
                                        <div style={{ padding: '10px 15px', color: '#666', fontStyle: 'italic' }}>
                                            No times found matching "{endTimeSearch}"
                                        </div>
                                    )}
                                    </div>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer className="modal-header-bg justify-content-center">
                        <Button variant="primary" type="submit" className="me-2">
                            Save
                        </Button>
                        <Button variant="danger" onClick={() => { resetAppointmentModalState(); onHide(); }}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
            <CustomSuccessModal
                show={showLocationSuccessModal}
                onHide={() => { 
                    setShowLocationSuccessModal(false); 
                    setLocationSuccessMessage(''); 
                    locationModalShownRef.current = false;
                }}
                message={locationSuccessMessage}
            />
            <CustomSuccessModal
                show={showAppointmentSuccessModal}
                onHide={() => { 
                    setShowAppointmentSuccessModal(false); 
                    setAppointmentSuccessMessage(''); 
                    appointmentModalShownRef.current = false;
                }}
                message={appointmentSuccessMessage}
            /> 
            <style>
                {`
                    .dropdown-menu {
                        max-height: 200px;
                        overflow-y: auto;
                    }
                    .dropdown-item {
                        cursor: pointer;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                    .dropdown-item:hover {
                        background-color: #f0f4fa;
                        color: var(--pmr-text-heading);
                    }
                    .btn-outline-primary {
                        border-color: #bfc9d9;
                        background: #fff;
                        color: var(--pmr-text-heading);
                    }
                    .btn-outline-primary:hover {
                        background-color: #e6f0fa;
                        color: var(--pmr-text-heading);
                    }
                    #dropdown-location {
                        color: var(--pmr-text-heading) !important;
                    }
                    .btn-outline-primary .fa-plus {
                        color: var(--pmr-primary) !important;
                    }
                    #dropdown-location:focus,
                    #dropdown-location:hover,
                    .btn-outline-primary:focus,
                    .btn-outline-primary:hover {
                        background-color: #fff !important;
                        border-color: #bfc9d9 !important;
                        color: var(--pmr-text-heading) !important;
                        box-shadow: none !important;
                    }
                    .error-border { border: 1px solid #dc3545 !important; box-shadow: none !important; }
                    
                    /* Hide scrollbar for time dropdowns */
                    .dropdown-menu::-webkit-scrollbar {
                        display: none;
                    }
                    
                                         .dropdown-menu {
                         -ms-overflow-style: none;  /* IE and Edge */
                         scrollbar-width: none;  /* Firefox */
                     } 
                     .flatpickr-calendar {
                    z-index: 999999 !important;
                    position: fixed !important;
                }
                 `}
            </style>
        </>
    );
};

export default AppointmentModal;